<script setup>
import { computed, defineEmits, defineProps, ref } from "vue";

const props = defineProps({
  carregando: {
    type: Boolean,
    default: false
  },
  orcamentos: {
    type: Array,
    default: () => []
  },
  usuarioNome: {
    type: String,
    default: ""
  }
});

const emits = defineEmits([
  "novo",
  "visualizar",
  "editar",
  "deletar",
  "recarregar"
]);

const filtroCodigo = ref(null);
const filtroCliente = ref(null);

const orcamentosFiltrados = computed(() => {
  return (props.orcamentos || []).filter((item) => {
    const validoCodigo = !filtroCodigo.value || item?.code === filtroCodigo.value?.code;
    const validoCliente = !filtroCliente.value || item?.clienteNome === filtroCliente.value?.clienteNome;
    return validoCodigo && validoCliente;
  });
});

const headers = [
  { text: "", value: "controles", sortable: false, width: "150px" },
  { text: "Numero Proposta", value: "numeroProposta" },
  { text: "Revisão", value: "revisao" },
  { text: "Sigla", value: "sigla" },
  { text: "Cliente", value: "clienteNome" },
  { text: "Referencia do Cliente", value: "referenciaCliente" },
  { text: "Contato Comercial", value: "contatoNome" },
  { text: "Contato Técnico", value: "contatoTecnicoNome" },
  { text: "Data Geração", value: "dateGenerate" },
  { text: "Valor de Venda s/IPI c/ICMS", value: "precoVenda_sIPI_cICMS" },
  { text: "Descrição do Status", value: "statusDescription" }
];

const formatarData = (data) => {
  if (!data) return "";
  const dataConvertida = new Date(data);
  if (Number.isNaN(dataConvertida.getTime())) return data;
  return dataConvertida.toLocaleDateString("pt-BR");
};

const formatarMoeda = (valor) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(valor || 0));
};

const corStatus = (statusDescricao) => {
  const texto = String(statusDescricao || "").toUpperCase();
  if (texto.includes("APROV")) return "success";
  if (texto.includes("PERD") || texto.includes("RECUS")) return "error";
  if (texto.includes("ENVIAD") || texto.includes("EXECU")) return "info";
  if (texto.includes("CANCEL")) return "grey";
  return "warning";
};
</script>

<template>
  <v-card class="c-card">
    <v-card-title class="c-card__header">
      <div class="c-header__buscas">
        <v-autocomplete
          class="c-busca__input"
          label="N°"
          :items="props.orcamentos"
          item-text="code"
          return-object
          clearable
          outlined
          hide-details
          v-model="filtroCodigo"
        />

        <v-autocomplete
          class="c-busca__input"
          label="Cliente"
          :items="props.orcamentos"
          item-text="clienteNome"
          return-object
          clearable
          outlined
          hide-details
          v-model="filtroCliente"
        />
      </div>

      <v-chip class="c-header__usuario" label>
        <v-icon left>mdi-account-circle-outline</v-icon>
        Usuario: {{ props.usuarioNome || "-" }}
      </v-chip>
    </v-card-title>

    <v-card-text>
      <div class="c-acoes mb-3">
        <v-btn color="primary" dark :loading="props.carregando" @click="emits('novo')">
          <v-icon left>mdi-plus</v-icon>
          Nova Solicitação
        </v-btn>
        <v-btn outlined color="primary" :loading="props.carregando" @click="emits('recarregar')">
          <v-icon left>mdi-refresh</v-icon>
          Recarregar
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="orcamentosFiltrados"
        :loading="props.carregando"
        item-key="_listKey"
        rows-per-page-text="Linhas por página:"
        page-text="{0}-{1} de {2}"
        class="c-grid elevation-1"
      >
        <!-- Coluna de Controles (botões inline) -->
        <template v-slot:item.controles="{ item }">
          <div class="c-controles">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1"
                  fab
                  dark
                  small
                  color="#32434D"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="emits('visualizar', item)"
                >
                  <v-icon dark>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Visualizar</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1"
                  fab
                  dark
                  small
                  color="#32434D"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="emits('editar', item)"
                >
                  <v-icon dark>mdi-file-edit-outline</v-icon>
                </v-btn>
              </template>
              <span>Editar</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1"
                  fab
                  dark
                  small
                  color="#B71C1C"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="emits('deletar', item)"
                >
                  <v-icon dark>mdi-trash-can</v-icon>
                </v-btn>
              </template>
              <span>Deletar</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Sigla -->
        <template v-slot:item.sigla="{ item }">
          {{
            [
              item.tipoDeEquipamentoSigla || item._mirror_tipoDeEquipamento?.sigla || item._mirror_tipoDeEquipamento?.codigo || item.sigla,
              item.tipoDeEquipamentoDescricao || item._mirror_tipoDeEquipamento?.descricao || item._mirror_tipoDeEquipamento?.nome || item._mirror_tipo || ''
            ].filter(Boolean).join(' - ') || '-'
          }}
        </template>

        <!-- Numero Proposta -->
        <template v-slot:item.numeroProposta="{ item }">
          {{ item.numeroProposta || item.numeroSolicitacao || item.code || "-" }}
        </template>

        <!-- Referencia do Cliente -->
        <template v-slot:item.referenciaCliente="{ item }">
          {{ item.referenciaCliente || item.cliente_referencia || "-" }}
        </template>

        <!-- Contato Comercial -->
        <template v-slot:item.contatoNome="{ item }">
          {{ item.contatoNome || item.contatoComercialNome || "-" }}
        </template>

        <!-- Contato Técnico -->
        <template v-slot:item.contatoTecnicoNome="{ item }">
          {{ item.contatoTecnicoNome || "-" }}
        </template>

        <!-- Data Geração -->
        <template v-slot:item.dateGenerate="{ item }">
          {{ formatarData(item.dateGenerate) }}
        </template>

        <!-- Valor -->
        <template v-slot:item.precoVenda_sIPI_cICMS="{ item }">
          {{ formatarMoeda(item.precoVenda_sIPI_cICMS || item.valorEstimado) }}
        </template>

        <!-- Status -->
        <template v-slot:item.statusDescription="{ item }">
          <v-chip small dark :color="corStatus(item.statusDescription)">
            {{ item.statusDescription }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.c-card__header {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: start;
}

.c-header__buscas {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.c-header__usuario {
  min-height: 48px;
}

.c-acoes {
  display: flex;
  gap: 10px;
}

/* Header igual ao Job: fundo azul escuro #230bc1, texto branco */
.c-grid :deep(thead tr th) {
  background-color: #230bc1 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  white-space: nowrap;
}

/* Ícone de ordenação branco */
.c-grid :deep(thead tr th .v-icon) {
  color: #ffffff !important;
}

/* Overflow horizontal */
.c-grid :deep(.v-data-table__wrapper) {
  overflow-x: auto;
}

/* Botões inline no estilo Job */
.c-controles {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 0;
}
</style>
